<style scoped>
 ol li:first-child {
  margin-top: 5px;
 }
 ol li {
  margin-bottom: 10px;
 }
</style>
<template>
  <div>
    <ValidationProvider
      v-if="(waterFeatureTypeId === waterFeatureTypeIdConfig) && !showOnlySWS75"
      name="SWS 250Ft Compliance"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <div role="group" class="form-row form-group justify-content-between">
        <label id="sws250ComplianceLabel" for="sws250Compliance">
          <div class="font-weight-normal">You have indicated that the proposed Harvest Activities will be within the shoreland area.**</div>
          <div class="pt-2">
            <span class="req-ind">*</span>
            Please choose only one option that will be used to comply with Maine Forest Service Chapter 21,
            <a :href="links.chapter21Section7" target="_blank" rel="noopener noreferrer">Section 7</a>
            rules for Shade and Tree Retention 250-foot Standard, for all 250-foot shoreland area within this harvest area.
          </div>
        </label>
        <b-form-radio-group
          id="sws250Compliance"
          name="sws250Compliance"
          v-model="harvestDetails.sws250ComplianceTypeId"
          stacked
          :disabled="mfsAdmin"
          :aria-describedby="$errorsIdString(errors, 'sws250Compliance')"
          aria-labelledby="sws250ComplianceLabel"
        >
          <b-form-radio
            v-for="swsComplianceType in swsComplianceTypes"
            :key="swsComplianceType.id"
            class="noselect"
            :value="swsComplianceType.id"
          >
            <span class="sr-only">Choose 250-foot Statewide Standard compliance type</span>
            {{ swsComplianceType.name }}
          </b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="sws250Compliance"
          class="d-block invalid-feedback"
        />
      </div>
    </ValidationProvider>
    <ValidationProvider
      v-if="(waterFeatureTypeId === waterFeatureTypeIdConfig) && !showOnlySWS250"
      name="SWS 75Ft Compliance"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <div role="group" class="form-row form-group justify-content-between">
        <label id="sws75ComplianceLabel" for="sws75Compliance">          
          <div class="font-weight-normal">You have indicated that the proposed Harvest Activities will be within the shoreland area.**</div>
          <div class="pt-2">
            <span class="req-ind">*</span>
            <span>
              Please choose only one option that will be used to comply with Maine Forest Service Chapter 21,
              <a :href="links.chapter21Section8" target="_blank" rel="noopener noreferrer">Section 8</a>
              rules for Shade and Tree Retention 75-foot Standard, for all 75-foot shoreland area within this harvest area.
            </span>
          </div>
        </label>
        <b-form-radio-group
          id="sws75Compliance"
          name="sws75Compliance"
          v-model="harvestDetails.sws75ComplianceTypeId"
          stacked
          :disabled="mfsAdmin"
          aria-labelledby="sws75ComplianceLabel"
        >
          <b-form-radio
            v-for="swsComplianceType in swsComplianceTypes"
            :key="swsComplianceType.id"
            class="noselect"
            :value="swsComplianceType.id"
          >
            <span class="sr-only">Choose 75-foot Statwide Standard compliance type</span>
            {{ swsComplianceType.name }}
          </b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="sws75Compliance"
          class="d-block invalid-feedback"
        />
      </div>
    </ValidationProvider>
    <div class="font-sm">
      <div class="font-weight-bold">*Definition of water bodies:</div>
      <div class="pt-1">Water body means any river, stream, stream channel, brook, freshwater wetland, coastal wetland, pond, lake, Great Pond, or any other hydrological feature regulated by this rule.</div>
    </div>
    <div class="font-sm pt-2">
      <div class="font-weight-bold">**Definition of a shoreland area:</div>
      <div class="pt-1">
          <span>Shoreland area means all land areas within:</span>
          <ol>
            <li>
                <span>250 feet, horizontal distance, of the normal high water line of:</span>
                <ol type="a">
                  <li>Rivers below the 50 square mile drainage point in the jurisdiction of the Land Use Planning Commission;</li>
                  <li>Rivers below the 25 square mile drainage point in municipalities not subject to the jurisdiction of the Land Use Planning Commission;</li>
                  <li>Great Ponds and nonforested freshwater wetlands 10 acres or larger;</li>
                  <li>Any coastal wetland;</li>
                  <li>Any size pond or freshwater wetland identified by the Department of Inland Fisheries and Wildlife as significant wildlife habitat or essential wildlife habitat;</li>
                </ol>
              </li>
            <li>
              <span>75 feet, horizontal distance, of the normal high water line of:</span>
              <ol type="a">
                <li>Streams between the 300 acre drainage point and the 50 square mile drainage point in the jurisdiction of the Land Use Planning Commission;</li>
                <li>Streams between the 300 acre drainage point and the 25 square mile drainage point in municipalities not subject to the jurisdiction of the Land Use Planning Commission;</li>
              </ol>
            </li>
            <li>
              <span>Adjacent to:</span>
              <ol type="a">
                <li>
                  <div>The normal high water line of streams above the 300 acre drainage point;</div>
                </li>
              </ol>
              <div class="pl-4">and,</div>
              <ol type="a" start="2">
                <li>
                  <span>Ponds or freshwater wetlands larger than 4,300 square feet but less than 10 acres that are not identified by the Department of Inland Fisheries and Wildlife as significant wildlife habitat or essential wildlife habitat.</span>
                </li>
              </ol>              
            </li>
          </ol>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'HarvestSWSForm',
  props: {
    harvestDetails: {
      type: Object,
      default() {
        return {
          sws250ComplianceTypeId: null,
          sws75ComplianceTypeId: null,
        };
      },
    },
    waterFeatureTypeId: {
      type: Number,
      required: false,
      default() {
        return 0;
      },
    },
  },
  computed: {
    ...mapState('ActivityDetails', {
      requirements: state => state.requirements,
    }),
    ...mapState('Configuration', {
      links: state => state.links,
      waterFeatureTypeIdConfig: state => state.waterFeatureTypeId,
    }),
    ...mapState(LookupModules.SWS_COMPLIANCE_TYPES.namespace, {
      swsComplianceTypes: state => state.options,
      swsComplianceTypesLoading: state => state.loading,
    }),
    ...mapGetters('User', ['mfsAdmin']),
    showOnlySWS75() {
      return this.requirements.harvestSWS75Required && !this.requirements.harvestSWS250Required
    },
    showOnlySWS250() {
      return this.requirements.harvestSWS250Required && !this.requirements.harvestSWS75Required
    }
  },
  watch: {
    waterFeatureTypeId() {
      this.harvestDetails.sws250ComplianceTypeId = null;
      this.harvestDetails.sws75ComplianceTypeId = null;
    },
  },
  created() {
    this.$store.dispatch(`${LookupModules.SWS_COMPLIANCE_TYPES.namespace}/fetchOptions`);
  },
};
</script>
